import Box from '../Box/Box';
import { Body1, Caption, Headline } from '../Typography/Typography';
import { rem } from 'polished';
import React from 'react';
import Image from '../Image/Image';
import Inline from '../Inline/Inline';
import { Tag } from '../Tag/Tag';
import { css } from 'styled-components';
import { theme } from '../../../config/theme';
import { formatDate } from '../../utils/formatDate';
import { formatTagList } from '../../utils/formatTagList';
import { calculateReadTime } from '../../utils/calculateReadTime';
import { Link } from 'gatsby';
import ChevronRight from '../../images/icons/chevron-right.svg';
import { paths } from '../../../config/paths';
import useViewport from '../../hooks/useViewport';

const postCardTransition = 'all 120ms ease';

export const PostCard = ({
    post,
    tagColorScheme = 'secondary',
    isFeaturedPosts = false,
    ...props
}) => {
    const {
        data: { title, date_published, tags, image, body },
    } = post;
    const formattedDatePublished = formatDate(date_published);
    const postTagList = formatTagList(tags);
    const readTime = calculateReadTime(body);
    const { isDesktop } = useViewport();

    return (
        <Box
            as={Link}
            to={`/blog/${post.uid}`}
            display="flex"
            flexDirection="column"
            p={rem(16)}
            boxShadow="primary-thin"
            borderRadius="sm"
            backgroundColor="neutral.white"
            transition={postCardTransition}
            {...(isDesktop && {
                _hover: {
                    boxShadow: 'accent-normal',
                    backgroundColor: 'primary.50',
                    '.postCard__cta': {
                        color: theme.colors.primary.main,
                    },
                    '.postCard__cta > p': {
                        textDecoration: 'underline',
                    },
                    '.postCard__cta > svg': {
                        transform: 'translateX(6px)',
                    },
                },
            })}
            {...props}
        >
            <Image
                objectFit="cover"
                objectPosition="center center"
                img={image}
                alt={title}
                height={rem(isFeaturedPosts ? 180 : 242)}
                width="100%"
                css={css`
                    transition: all 120ms ease;
                    background-color: ${({ theme }) =>
                        theme.colors.neutral.white};
                    border-radius: ${({ theme }) => theme.radii.sm};
                `}
            />
            <Inline gap={rem(8)} mt={rem(8)}>
                {postTagList.map((tag) => (
                    <Tag
                        tag={tag}
                        colorScheme={tagColorScheme}
                        path={paths.blogTag}
                    />
                ))}
            </Inline>
            <Box mt={rem(16)} color="neutral.1400">
                <Caption color="currentColor">
                    Published {formattedDatePublished}
                </Caption>{' '}
                <Caption color="currentColor">-</Caption>{' '}
                <Caption color="currentColor">{readTime} read</Caption>
            </Box>
            <Headline mt={rem(8)} fontWeight="500" maxWidth={rem(422)}>
                {title}
            </Headline>
            <Box mt="auto">
                <Box
                    className="postCard__cta"
                    display="flex"
                    alignItems="center"
                    gap={rem(8)}
                    mt={rem(16)}
                >
                    <Body1 bold color="currentColor">
                        Read now
                    </Body1>
                    <Box
                        transition={postCardTransition}
                        as={ChevronRight}
                        size={rem(20)}
                        color="currentColor"
                    />
                </Box>
            </Box>
        </Box>
    );
};
